import { useState } from 'react';
import type { RefundDetail, RefundPage, SettlementStatement } from '@gatecheckpro/contracts';
import { Button, Card, EmptyState, LoadingRegion, Notice } from '@gatecheckpro/design-system';
import { useResource } from '../../lib/useResource.ts';
import { useCommand } from '../../lib/useCommand.ts';
import { FailureNotice } from '../../lib/refusal.tsx';
import { RefundHistory, RefundSummary, ticketsAreVoid } from './refund-status.tsx';
import { SettlementBreakdown } from './settlement.tsx';
import { OrderSearch } from './OrderSearch.tsx';
import { OrderRefundPanel } from './OrderRefundPanel.tsx';
import { RefundQueue } from './RefundQueue.tsx';

/**
 * An organizer's finance surface: what the ledger holds, and one refund at a
 * time.
 *
 * Two shapes here are consequences of what the server actually offers rather
 * than of what would be nicest, and both are deliberate.
 *
 * **One statement per currency, never a total.** The settlement read returns an
 * array with one entry per currency an organizer has collected in. Adding
 * ₦ to $ produces a number that is true of nothing, so the page renders each
 * position separately and offers no combined figure — not even a discreet one
 * at the bottom.
 *
 * **A refund starts from the order, the way the conversation does.** An
 * operator has an order number or the email a customer is calling from, so the
 * page begins there: find the order, read its authoritative ticket records,
 * choose from them, raise. Nothing on this path asks for an identifier the
 * caller would have to already possess.
 *
 * Nothing here decides whether a refund may be raised, approved or retried. The
 * buttons post the command and render what comes back, including the refusal.
 */
export function FinancePage({ organizationId }: { organizationId: string }): React.JSX.Element {
  const base = `/api/v1/organizer/organizations/${organizationId}`;
  const settlement = useResource<{ statements: SettlementStatement[] }>(`${base}/settlement`);

  const [openOrderId, setOpenOrderId] = useState<string | null>(null);
  const [openRefundId, setOpenRefundId] = useState<string | null>(null);
  const queue = useResource<RefundPage>(`${base}/finance/refunds`);
  const refund = useResource<RefundDetail>(
    openRefundId === null ? null : `${base}/refunds/${openRefundId}`,
  );

  return (
    <div className="gc-stack gc-stack--loose">
      <section className="gc-stack">
        <div className="gc-page-header">
          <span className="gc-eyebrow">Finance</span>
          <h1>Money collected and returned</h1>
        </div>

        {settlement.resource.status === 'loading' && (
          <LoadingRegion label="Loading the settlement position" />
        )}
        {settlement.resource.status === 'failed' && (
          <FailureNotice error={settlement.resource.error} />
        )}
        {settlement.resource.status === 'loaded' &&
          (settlement.resource.data.statements.length === 0 ? (
            <EmptyState title="Nothing collected yet">
              When payments settle, what was collected and what went back appears here.
            </EmptyState>
          ) : (
            /*
             * One card per currency, and no aggregate anywhere.
             *
             * An organizer selling in naira and dollars has two positions, not
             * one position with a converted total — the platform holds no rate,
             * and a client that picked one would be inventing the most
             * consequential number on the screen.
             */
            settlement.resource.data.statements.map((statement) => (
              <Card
                key={statement.position.currency}
                title={`Position in ${statement.position.currency}`}
                eyebrow="From the accounting ledger"
              >
                <SettlementBreakdown position={statement.position} />
              </Card>
            ))
          ))}
      </section>

      <section className="gc-stack">
        {/*
          Discovery, not an identifier box.
          
          What stood here asked for a refund's primary key and admitted on
          screen that searching was not built. Both are gone rather than
          adapted: they existed only to be honest about a gap, and the gap has
          closed.
        */}
        <OrderSearch base={base} onOpen={(order) => setOpenOrderId(order.orderId)} />

        {openOrderId !== null && (
          <OrderRefundPanel
            base={base}
            orderId={openOrderId}
            onRaised={(raised) => {
              setOpenRefundId(raised.id);
              settlement.reload();
              queue.reload();
            }}
          />
        )}

        <RefundQueue base={base} page={queue} onOpen={(refundId) => setOpenRefundId(refundId)} />

        {/*
          Nothing is open, so nothing is loading.

          `useResource(null)` never settles and therefore reads as `loading` for
          ever — which rendered a permanent "Loading the refund" on a page where
          no refund had been chosen. A spinner for a request nobody made is a
          lie about what the product is doing.
        */}
        {openRefundId !== null && refund.resource.status === 'loading' && (
          <LoadingRegion label="Loading the refund" />
        )}
        {openRefundId !== null && refund.resource.status === 'failed' && (
          <FailureNotice error={refund.resource.error} />
        )}
        {openRefundId !== null && refund.resource.status === 'loaded' && (
          <RefundPanel
            base={base}
            refund={refund.resource.data}
            reload={() => {
              refund.reload();
              settlement.reload();
              queue.reload();
            }}
          />
        )}
      </section>
    </div>
  );
}

/**
 * One refund, and the actions that mean anything in its current state.
 *
 * Split out so the loaded value narrows once, at the boundary, rather than
 * inside every callback — and so each action reads as what it does to a
 * customer rather than as a state transition.
 */
function RefundPanel({
  base,
  refund,
  reload,
}: {
  base: string;
  refund: RefundDetail;
  reload: () => void;
}): React.JSX.Element {
  const decide = useCommand<RefundDetail>();
  const payout = useCommand<RefundDetail>();
  const requery = useCommand<RefundDetail>();

  /*
   * Re-read on success, and only on success.
   *
   * Re-reading is right: what a refund is after a command is the server's
   * account of it, and a client stitching its own from the response would
   * eventually show a state the server never held.
   *
   * Reloading after a *failure* is wrong, and silently so. The reload puts the
   * page back into `loading`, which unmounts this panel and takes the command's
   * error with it — so the button appeared to do nothing at all. That is how it
   * behaved when a provider that cannot refund declined the payout: no state
   * change, no message, no explanation. `run` returns null when the command
   * failed, which is the signal to leave the refusal on screen.
   */
  const after = (promise: Promise<unknown>): void => {
    void promise.then((result) => {
      if (result !== null) reload();
    });
  };

  return (
    <Card title="Refund" eyebrow={refund.refundNumber}>
      <div className="gc-stack">
        <RefundSummary refund={refund} />

        {/*
          The decision, offered only where it means anything. A button outside
          REQUESTED is one the server is about to refuse with
          REFUND_NOT_PENDING_DECISION.
        */}
        {refund.status === 'REQUESTED' && (
          <div className="gc-row">
            <Button
              variant="primary"
              busy={decide.busy}
              onClick={() =>
                after(
                  decide.run(`${base}/refunds/${refund.id}/decision`, {
                    method: 'POST',
                    body: { decision: 'APPROVE', note: null },
                  }),
                )
              }
            >
              Approve
            </Button>
            <Button
              variant="secondary"
              busy={decide.busy}
              onClick={() =>
                after(
                  decide.run(`${base}/refunds/${refund.id}/decision`, {
                    method: 'POST',
                    body: { decision: 'REJECT', note: null },
                  }),
                )
              }
            >
              Decline
            </Button>
          </div>
        )}

        {/*
          Returning the customer's payment through the provider. Named for what
          it is: this has nothing to do with paying an organizer their proceeds,
          which is a different slice and must not borrow this vocabulary.
        */}
        {refund.status === 'APPROVED' && (
          <Button
            variant="primary"
            busy={payout.busy}
            onClick={() =>
              after(payout.run(`${base}/refunds/${refund.id}/payout`, { method: 'POST' }))
            }
          >
            Send the refund to the provider
          </Button>
        )}

        {(refund.status === 'PROVIDER_PENDING' ||
          refund.status === 'FAILED' ||
          refund.status === 'REVIEW_REQUIRED') && (
          <Button
            variant="secondary"
            busy={requery.busy}
            onClick={() =>
              after(requery.run(`${base}/refunds/${refund.id}/requery`, { method: 'POST' }))
            }
          >
            Ask the provider again
          </Button>
        )}

        {ticketsAreVoid(refund) && (
          <Notice tone="caution" title="These tickets are already void">
            The holders cannot get in with them, whatever happens to the payout next.
          </Notice>
        )}

        <FailureNotice error={decide.error} />
        <FailureNotice error={payout.error} />
        <FailureNotice error={requery.error} />

        <RefundHistory refund={refund} />
      </div>
    </Card>
  );
}
